import PrintSettingsCache from "../../../components/printSettings/cache/PrintSettingsCache";
import { EventEditionDetails } from "../../types";
import { ReceiptBuilder, BusinessUnit, ReceiptBuilderProps } from "@reedexpo/mercury-badge";
import { PrintMethods } from "../../enums";
import print from "print-js";
import { useSelector } from "react-redux";
import { eventEditionSelector } from "../../../redux/eventEdition/selectors/eventEdition.selectors";
import { PrintSettingsCacheValue } from "../../../components/printSettings/types";
import { BadgeDownloadOptions } from "../../../components/search/details/download/types";
import PrintService from "../../../services/PrintService";
import { SNACKBAR_VARIANTS } from "../../constants";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import getReceiptDateTime from "../../utils/getReceiptDateTime";
import JapaneseFont from "../../../common/badges/builder/assets/NotoSansJP-Regular.ttf";
import JapaneseBoldFont from "../../../common/badges/builder/assets/NotoSansJP-Bold.ttf";
import NotoSansFont from "../../../common/badges/builder/assets/NanumGothic.ttf";
import { PrintReceiptParams } from "./receipts.types";
import { useReceiptPropGeneration } from "./useReceiptPropGeneration/useReceiptPropGeneration";
import EventHelper from "../../network/helpers/event/EventHelper";
import { useCmsImages } from "../../../components/entry/hooks/useCmsImages/useCmsImages";
import { ZPLBadge } from "../../badges/builder/types";
import store from "../../../redux/store";
import getCountryISO2 from "country-iso-3-to-2";
import ChineseSimplifiedFont from "../../badges/builder/assets/NotoSansCS-Regular.ttf";
import PublicApiService from "../../../services/PublicApiService";

const useReceiptPrint = () => {
  const eventEditionDetails = useSelector(eventEditionSelector).eventEdition as EventEditionDetails;
  const { enqueueSnackbar } = useSnackbar();
  const { i18n, t } = useTranslation("common");
  const { getPropsForUpgrades, getPropsForPartialPayments } = useReceiptPropGeneration();
  const { getReceiptLogoUrl } = useCmsImages();

  const printWithServer = async (receipt: Blob, cachedOptions: PrintSettingsCacheValue, options: BadgeDownloadOptions = {}) => {
    try {
      const isServerConnected = await PrintService.isPrintServerConnected();
      if (!isServerConnected) {
        await PrintService.setupPdfWS({ useCache: true });
      }
      PrintService.print(receipt, { selectedPrinter: cachedOptions.settingsValues.selectedPrinter });
      if (!options.hideToastMessage) {
        enqueueSnackbar(<b>{t("common.badgeSentToPrinter")}</b>, SNACKBAR_VARIANTS.success);
      }
    } catch (e) {
      enqueueSnackbar(<b>{t("errors.unableToConnectToPrintServer")}</b>, SNACKBAR_VARIANTS.error);
    }
  };

  const getReceiptBlob = async (receiptParams: PrintReceiptParams) => {
    const { badge, options, price } = receiptParams;
    const currency = EventHelper.eventEdition!.defaultCurrencyCode;
    const upgradePrice = { currency, value: price };
    const customBadgeUpgradePrice = options?.customBadgeUpgradePrice;
    const logoUrl = await getReceiptLogoUrl(i18n.language);
    const props = options?.partialPaymentsData
      ? getPropsForPartialPayments(options)
      : getPropsForUpgrades({ ...receiptParams, upgradePrice, customBadgeUpgradePrice });

    const blob = await ReceiptBuilder.getBlob({
      ...props,
      country: getCountryISO2(store.getState().eventEdition.cmsEventDetails?.siteDetails.address.country || ""),
      currency: EventHelper.eventEdition?.defaultCurrencyCode || "",
      businessUnit: EventHelper.eventEdition!.businessUnitGbsCode as BusinessUnit,
      locale: i18n.language,
      logo: logoUrl,
      date: getReceiptDateTime(),
      companyName: badge.organisation.displayName,
      fonts: [
        {
          name: "JAPANESE",
          fonts: [{ src: ChineseSimplifiedFont }, { src: JapaneseFont }]
        },
        { name: "Noto Sans", src: NotoSansFont },
        { name: "JAPANESE_BOLD_800", src: JapaneseBoldFont }
      ]
    } as ReceiptBuilderProps);
    return { blob };
  };

  const directReceiptPrint = (receiptBlob: Blob) => {
    const receiptUrl = URL.createObjectURL(receiptBlob);
    print(receiptUrl);
    URL.revokeObjectURL(receiptUrl);
  };

  const printWithServerWithOptions = async (receiptBlob: Blob) => {
    const cachedOptions = PrintSettingsCache.get();

    await printWithServer(receiptBlob, cachedOptions);
    if (EventHelper.isAustraliaBu()) {
      await PrintService.printZPL((receiptBlob as unknown as ZPLBadge)?.z64, {
        selectedPrinter: cachedOptions.settingsValues.selectedPrinter
      });
      return;
    }
    if (EventHelper.isFranceBu() || EventHelper.isUkBu()) {
      await printWithServer(receiptBlob, cachedOptions);
    }
  };

  const printReceipt = async (printReceiptParams: PrintReceiptParams) => {
    const cachedOptions = PrintSettingsCache.get();
    const { badgeId, orderId } = printReceiptParams;
    const { blob: receiptBlob } = await getReceiptBlob(printReceiptParams);
    const gbsCode = EventHelper.eventEdition?.gbsCode ?? "";
    if (cachedOptions.printMethod === PrintMethods.PRINT_DIRECT && eventEditionDetails?.businessUnitGbsCode !== BusinessUnit.AUSTRALIA) {
      directReceiptPrint(receiptBlob);
    } else {
      await printWithServerWithOptions(receiptBlob);
    }
    const file = new File([receiptBlob], `receipt-${orderId}.pdf`, { type: "application/pdf" });
    await PublicApiService.saveReceipt(file, gbsCode, orderId, badgeId);
  };

  const fetchBlobFromUrl = async (downloadUrl: string): Promise<Blob> => {
    const response = await fetch(downloadUrl);
    return await response.blob();
  };

  const printDirectlyFromUrl = async (downloadUrl: string) => {
    try {
      const blob = await fetchBlobFromUrl(downloadUrl);
      const blobUrl = URL.createObjectURL(blob);
      print({
        printable: blobUrl,
        type: "pdf",
        showModal: false
      });

      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Failed to print receipt:", err);
    }
  };

  const printReceiptFromDownloadUrl = async (downloadUrl: string) => {
    const cachedOptions = PrintSettingsCache.get();
    const blob = await fetchBlobFromUrl(downloadUrl);
    if (cachedOptions.printMethod === PrintMethods.PRINT_DIRECT && eventEditionDetails?.businessUnitGbsCode !== BusinessUnit.AUSTRALIA) {
      await printDirectlyFromUrl(downloadUrl);
    } else {
      await printWithServerWithOptions(blob);
    }
  };

  return {
    printReceipt,
    printWithServer,
    fetchBlobFromUrl,
    printDirectlyFromUrl,
    getReceiptBlob,
    printWithServerWithOptions,
    directReceiptPrint,
    printReceiptFromDownloadUrl
  };
};

export default useReceiptPrint;
